import axiosInstance from "../../../middlewares/axiosInstance";
import { AxiosResponse } from "axios";
import {
  StockTransfer,
  StockTransferListResponse,
  CreateStockTransferRequest,
  UpdateStockTransferRequest,
  StockTransferFilters,
  DispatchTransferRequest,
  ReceiveTransferRequest,
  LocationOption,
  ProductOption,
  BatchOption,
  TransferValidationResponse,
  StockTransferResponse,
  StockTransferListApiResponse,
  LocationsResponse,
  ProductsResponse,
  BatchesResponse,
} from "../types/stock-transfer.types";

class StockTransferService {
  private baseUrl = "/business-admin/stock-transfers";
  private baseUrl2 = "/public";

  // Stock Transfer CRUD Operations
  async createStockTransfer(
    data: CreateStockTransferRequest
  ): Promise<StockTransferResponse> {
    try {
      const response: AxiosResponse<StockTransferResponse> =
        await axiosInstance.post(`${this.baseUrl}/create`, data);
      return response.data;
    } catch (error) {
      console.error("Error creating stock transfer:", error);
      throw error;
    }
  }

  async updateStockTransfer(
    transferId: string,
    data: Partial<UpdateStockTransferRequest>
  ): Promise<StockTransferResponse> {
    try {
      const requestBody = {
        transfer_id: transferId,
        ...data,
      };
      const response: AxiosResponse<StockTransferResponse> =
        await axiosInstance.post(`${this.baseUrl}/update`, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error updating stock transfer:", error);
      throw error;
    }
  }

  async getStockTransfer(transferId: string): Promise<StockTransferResponse> {
    try {
      const response: AxiosResponse<StockTransferResponse> =
        await axiosInstance.post(`${this.baseUrl}/get`, {
          transfer_id: transferId,
        });
      return response.data;
    } catch (error) {
      console.error("Error fetching stock transfer:", error);
      throw error;
    }
  }

  async deleteStockTransfer(
    transferId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response: AxiosResponse<{ success: boolean; message: string }> =
        await axiosInstance.post(`${this.baseUrl}/delete`, {
          transfer_id: transferId,
        });
      return response.data;
    } catch (error) {
      console.error("Error deleting stock transfer:", error);
      throw error;
    }
  }

  // Stock Transfer List with Filters
  async getStockTransfers(
    page: number = 1,
    pageSize: number = 10,
    filters: StockTransferFilters = {},
    sortField?: string,
    sortOrder?: "asc" | "desc"
  ): Promise<StockTransferListApiResponse> {
    try {
      const requestBody: any = {
        page,
        page_size: pageSize,
      };
      // Add filters
      if (filters.transfer_type && filters.transfer_type !== "All") {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.transfer_type = filters.transfer_type;
      }
      if (filters.status && filters.status !== "All") {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.status = filters.status;
      }
      if (filters.source_location) {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.source_location = filters.source_location;
      }
      if (filters.target_location) {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.target_location = filters.target_location;
      }
      if (filters.created_by) {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.created_by = filters.created_by;
      }
      if (filters.search) {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.search = filters.search;
      }
      if (filters.date_range?.start || filters.date_range?.end) {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.date_range = {
          start: filters.date_range?.start,
          end: filters.date_range?.end,
        };
      }
      // Add sorting
      if (sortField) {
        requestBody.sort_field = sortField;
      }
      if (sortOrder) {
        requestBody.sort_order = sortOrder;
      }
      const response: AxiosResponse<StockTransferListApiResponse> =
        await axiosInstance.post(`${this.baseUrl}/list`, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error fetching stock transfers:", error);
      throw error;
    }
  }

  // Transfer Actions
  async dispatchTransfer(
    transferId: string,
    data: DispatchTransferRequest
  ): Promise<StockTransferResponse> {
    try {
      const requestBody = {
        transfer_id: transferId,
        dispatch_notes: data.dispatch_notes,
      };
      const response: AxiosResponse<StockTransferResponse> =
        await axiosInstance.post(`${this.baseUrl}/dispatch`, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error dispatching transfer:", error);
      throw error;
    }
  }

  async receiveTransfer(
    transferId: string,
    data: ReceiveTransferRequest
  ): Promise<StockTransferResponse> {
    try {
      const requestBody = {
        transfer_id: transferId,
        received_items: data.received_items,
        receive_notes: data.receive_notes,
      };
      const response: AxiosResponse<StockTransferResponse> =
        await axiosInstance.post(`${this.baseUrl}/receive`, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error receiving transfer:", error);
      throw error;
    }
  }

  async cancelTransfer(
    transferId: string,
    cancellationReason: string
  ): Promise<StockTransferResponse> {
    try {
      const requestBody = {
        transfer_id: transferId,
        cancellation_reason: cancellationReason,
      };
      const response: AxiosResponse<StockTransferResponse> =
        await axiosInstance.post(`${this.baseUrl}/cancel`, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error cancelling transfer:", error);
      throw error;
    }
  }

  // Validation
  async validateTransfer(
    data: CreateStockTransferRequest
  ): Promise<{ success: boolean; data: TransferValidationResponse }> {
    try {
      const response: AxiosResponse<{
        success: boolean;
        data: TransferValidationResponse;
      }> = await axiosInstance.post(`${this.baseUrl}/validate`, data);
      return response.data;
    } catch (error) {
      console.error("Error validating transfer:", error);
      throw error;
    }
  }

  // Location Management
  async getLocations(
    storeId?: string
  ): Promise<{
    success: boolean;
    data: {
      branches?: LocationOption[];
      warehouses?: LocationOption[];
      [key: string]: any;
    };
  }> {
    try {
      const requestBody: any = {};
      if (storeId) {
        requestBody.store_id = storeId;
      }
      const response = await axiosInstance.post(
        `${this.baseUrl2}/locations/list`,
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching locations:", error);
      throw error;
    }
  }

  async getWarehouses(
    storeId?: string
  ): Promise<{
    success: boolean;
    data: {
      branches?: LocationOption[];
      warehouses?: LocationOption[];
      [key: string]: any;
    };
  }> {
    try {
      const requestBody: any = {};
      if (storeId) {
        requestBody.store_id = storeId;
      }
      const response = await axiosInstance.post(
        `${this.baseUrl2}/locations/list`,
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching warehouses:", error);
      throw error;
    }
  }

  // Product Management
  async getTransferableProducts(
    warehouseId: string,
    search?: string
  ): Promise<ProductsResponse> {
    try {
      const requestBody: any = {
        warehouse_id: warehouseId,
      };
      if (search) {
        requestBody.search = search;
      }
      const response: AxiosResponse<ProductsResponse> =
        await axiosInstance.post(`${this.baseUrl}/get-products`, requestBody);
      return response.data;
    } catch (error) {
      console.error("Error fetching transferable products:", error);
      throw error;
    }
  }

  async getProductStock(
    productId: string,
    warehouseId: string
  ): Promise<{
    success: boolean;
    data: {
      available_quantity: number;
      unit: string;
      is_batch_tracked: boolean;
      batches?: BatchOption[];
    };
  }> {
    try {
      const requestBody = {
        product_id: productId,
        warehouse_id: warehouseId,
      };
      const response = await axiosInstance.post(
        `${this.baseUrl}/get-product-stock`,
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching product stock:", error);
      throw error;
    }
  }

  // Batch Management
  async getProductBatches(
    productId: string,
    warehouseId: string
  ): Promise<BatchesResponse> {
    try {
      const requestBody = {
        product_id: productId,
        warehouse_id: warehouseId,
      };
      const response: AxiosResponse<BatchesResponse> = await axiosInstance.post(
        `${this.baseUrl}/get-batches`,
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching product batches:", error);
      throw error;
    }
  }

  async validateBatch(
    batchId: string,
    warehouseId: string
  ): Promise<{
    success: boolean;
    data: {
      is_valid: boolean;
      is_expired: boolean;
      remaining_quantity: number;
      expiry_date?: number;
    };
  }> {
    try {
      const requestBody = {
        batch_id: batchId,
        warehouse_id: warehouseId,
      };
      const response = await axiosInstance.post(
        `${this.baseUrl}/validate-batch`,
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error("Error validating batch:", error);
      throw error;
    }
  }

  // Transfer Statistics
  async getTransferStats(
    branchId?: string,
    dateRange?: { start: number; end: number }
  ): Promise<{
    success: boolean;
    data: {
      total_transfers: number;
      by_status: {
        draft: number;
        dispatched: number;
        received: number;
        cancelled: number;
      };
      by_type: {
        inter_branch: number;
        internal: number;
      };
      total_items_transferred: number;
      total_value_transferred: number;
      avg_processing_time: number;
    };
  }> {
    try {
      const requestBody: any = {};
      if (branchId) {
        requestBody.branch_id = branchId;
      }
      if (dateRange?.start || dateRange?.end) {
        requestBody.date_range = {
          start: dateRange?.start,
          end: dateRange?.end,
        };
      }
      const response = await axiosInstance.post(
        `${this.baseUrl}/get-stats`,
        requestBody
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching transfer stats:", error);
      throw error;
    }
  }

  // Export functionality
  async exportTransfers(
    filters: StockTransferFilters = {},
    format: "csv" | "excel" = "csv"
  ): Promise<Blob> {
    try {
      const requestBody: any = {
        format,
      };
      // Add filters
      if (filters.transfer_type && filters.transfer_type !== "All") {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.transfer_type = filters.transfer_type;
      }
      if (filters.status && filters.status !== "All") {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.status = filters.status;
      }
      if (filters.source_location) {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.source_location = filters.source_location;
      }
      if (filters.target_location) {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.target_location = filters.target_location;
      }
      if (filters.date_range?.start || filters.date_range?.end) {
        requestBody.filters = requestBody.filters || {};
        requestBody.filters.date_range = {
          start: filters.date_range?.start,
          end: filters.date_range?.end,
        };
      }
      const response = await axiosInstance.post(
        `${this.baseUrl}/export`,
        requestBody,
        { responseType: "blob" }
      );
      return response.data;
    } catch (error) {
      console.error("Error exporting transfers:", error);
      throw error;
    }
  }

  // Helper methods
  generateTransferNumber(transferType: "Inter-Branch" | "Internal"): string {
    const prefix = transferType === "Inter-Branch" ? "IBT" : "INT";
    const timestamp = new Date().getTime().toString(36).toUpperCase();
    return `${prefix}-${timestamp}`;
  }

  formatTransferSummary(items: any[]): string {
    if (!items || items.length === 0) return "No items";
    const visibleItems = items.slice(0, 2);
    const visibleNames = visibleItems.map(
      (item) => item.item_name || item.name
    );
    if (items.length <= 2) {
      return visibleNames.join(", ");
    }
    const remainingCount = items.length - 2;
    return `${visibleNames.join(", ")} (+${remainingCount} more)`;
  }

  calculateTransferValue(items: any[]): number {
    if (!items || items.length === 0) return 0;
    return items.reduce((total, item) => {
      const itemCost = (item.quantity || 0) * (item.unit_cost || 0);
      return total + itemCost;
    }, 0);
  }

  validateTransferItem(item: any, availableStock: number): string[] {
    const errors: string[] = [];
    if (!item.product || !item.product._id) {
      errors.push("Product is required");
    }
    if (!item.quantity || item.quantity <= 0) {
      errors.push("Quantity must be greater than 0");
    }
    if (item.quantity > availableStock) {
      errors.push(`Quantity exceeds available stock (${availableStock})`);
    }
    if (item.product?.is_batch_tracked && !item.batch) {
      errors.push("Batch selection is required for batch-tracked items");
    }
    return errors;
  }
}

export const stockTransferService = new StockTransferService();
